import { TextField } from '@mui/material';
import React, { useState, useEffect } from 'react';

interface MobileGeoLocationFieldProps {
  title: string;
  /** The captured "lat,lng" string, or empty before capture. */
  value?: string;
  onChange: (value: string) => void;
}

/** A technician in a basement or a carpark should get an answer, not a spinner. */
const GEOLOCATION_TIMEOUT_MS = 15000;
/** A fix from the last minute is good enough for recording where the work happened. */
const GEOLOCATION_MAX_AGE_MS = 60000;

export const InteractionGeoLocationField: React.FC<MobileGeoLocationFieldProps> = ({
  title,
  value,
  onChange,
}) => {
  const [loading, setLoading] = useState(false);

  const handleGetLocation = () => {
    if (!navigator.geolocation) {
      onChange('Geolocation not supported');
      return;
    }

    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        onChange(`${latitude},${longitude}`);
        setLoading(false);
      },
      (err) => {
        // Read `message` structurally rather than via `instanceof
        // GeolocationPositionError`: that global is not defined in every
        // environment, and the instanceof itself would then throw here — inside the
        // very handler meant to report the failure — leaving the field blank with no
        // answer recorded.
        const reason = typeof err?.message === 'string' ? err.message : 'Unknown error';
        console.error('Geolocation error:', reason);
        onChange('Location unavailable');
        setLoading(false);
      },
      // Without a timeout the request can stall indefinitely: neither callback fires,
      // the spinner never stops, and the field records nothing at all. A bounded wait
      // means a failure is reported as one.
      { timeout: GEOLOCATION_TIMEOUT_MS, maximumAge: GEOLOCATION_MAX_AGE_MS }
    );
  };

  useEffect(() => {
    if (!value) {
      handleGetLocation();
    }
    // Mount-only: capture once if the answer is still empty. Re-running on `value`
    // would fire a second capture the moment the first one resolves, and
    // `handleGetLocation` is redefined every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <TextField
      fullWidth
      size="small"
      label={title || 'Geo-Location'}
      value={value || (loading ? 'Getting location...' : '')}
      slotProps={{ input: { readOnly: true } }}
    />
  );
};
